import React, { useEffect, useState } from 'react';
import { fetchPageBySlug } from '../lib/cms';
import Metadata from '../components/Metadata';
import ContactForm from '../components/ContactForm';

export default function ContactPage() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchPageBySlug('kontakt');
        setPageData(page);
      } catch (error) {
        console.error('Error loading contact page:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[--color-brand-cream]">
        <div className="flex flex-col items-center gap-4">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-[--color-brand-green-dark]"></i>
          <p className="font-sans text-sm text-[--color-brand-muted] font-medium">Henter indhold...</p>
        </div>
      </div>
    );
  }

  const title = pageData?.meta_title || "Kontakt | BP haveservice";
  const description = pageData?.meta_description || "Har du spørgsmål eller kommentarer? Kontakt os i dag for at høre mere om, hvad vi kan gøre for dig.";

  const getOptimizedImageUrl = (url, width = 800) => {
    if (!url) return '';
    if (url.startsWith('https://assets.auxodev.dk/')) {
      return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
    }
    return url;
  };

  return (
    <>
      <Metadata title={title} description={description} />

      {/* Header Section */}
      <section className="relative bg-[--color-brand-cream] overflow-hidden py-16 lg:py-24 px-6 flex items-center border-b border-[--color-brand-sand]">
        {/* Diagonal Sage Backdrop Split (Hero Left) */}
        <div 
          className="absolute inset-y-0 left-0 w-full lg:w-3/5 -skew-x-12 origin-top-left -translate-x-12 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(135deg, rgba(141, 164, 126, 0.35) 0%, rgba(76, 110, 59, 0.15) 100%)' }}
        ></div>

        {/* The "Peeking" Circle (Bottom-Left) */}
        <div 
          className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full z-10 pointer-events-none"
          style={{ backgroundColor: 'rgba(45, 75, 28, 0.15)' }}
        ></div>

        {/* Organic Background Arch (Top Right) */}
        <div 
          className="absolute top-0 right-0 w-[30%] h-[50%] rounded-bl-[150px] z-10 pointer-events-none"
          style={{ backgroundColor: 'rgba(141, 164, 126, 0.12)' }}
        ></div>

        <div className="relative z-20 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Column: Editorial Headings */}
          <div className="lg:col-span-7 space-y-6">
            <span className="inline-flex items-center gap-2 font-sans font-bold tracking-widest text-xs uppercase text-[--color-brand-green-dark]">
              Kom I Dialog
            </span>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl text-[--color-brand-charcoal] font-bold leading-tight">
              {pageData?.title || 'Kontakt'}
            </h1>
            <p className="font-sans text-base sm:text-lg text-[--color-brand-muted] max-w-xl leading-relaxed">
              {pageData?.subtitle || 'Har du spørgsmål eller kommentarer? Kontakt os i dag'}
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <a
                href="tel:30441611"
                className="group inline-flex items-center justify-center px-8 py-3.5 rounded-full bg-white text-[--color-brand-green-dark] border border-[--color-brand-green-dark]/20 hover:bg-white hover:text-[--color-brand-green-medium] hover:border-[--color-brand-green-medium] transition-all duration-300 text-sm font-semibold tracking-wide shadow-md hover:shadow-lg hover:-translate-y-0.5 cursor-pointer"
              >
                <i className="fa-solid fa-phone mr-2.5 text-xs" />
                <span>Ring 30 44 16 11</span>
              </a>
            </div>
          </div>

          {/* Right Column: Beautiful Leaf-shaped Image */}
          <div className="lg:col-span-5 relative flex flex-col items-center justify-center w-full max-w-md mx-auto overflow-visible">
            {/* Matrix Dot Pattern */}
            <div className="absolute -top-4 -right-4 grid grid-cols-6 gap-3 w-28 opacity-30 text-[--color-brand-green-dark] pointer-events-none z-0">
              {Array.from({ length: 24 }).map((_, i) => (
                <span key={i} className="w-1.5 h-1.5 rounded-full bg-current"></span>
              ))}
            </div>

            {/* Leaf Image Container */}
            <div className="relative w-full aspect-[4/3] sm:aspect-[1.2] rounded-tl-[100px] rounded-br-[100px] rounded-tr-3xl rounded-bl-3xl overflow-hidden shadow-xl border-4 border-white z-10 bg-[--color-brand-sage-pale]/20">
              <img
                src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/istock-1315346350.jpg", 800)}
                alt="Kontakt BP Totalservice"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Decorative Leaf Element */}
            <div className="absolute -bottom-10 -left-10 w-24 h-24 pointer-events-none z-20 opacity-40">
              <img 
                src="https://assets.auxodev.dk/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/leaf-decor.png" 
                alt="Blad dekoration" 
                className="w-full h-full object-contain rotate-45"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Content & Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Contact Details */}
            <div className="lg:col-span-5 space-y-8">
              {pageData?.content ? (
                <div
                  className="prose-content bg-[--color-brand-cream]/40 p-8 rounded-3xl border border-[--color-brand-sand]"
                  dangerouslySetInnerHTML={{ __html: pageData.content }}
                />
              ) : (
                <div className="bg-[--color-brand-cream]/40 p-8 rounded-3xl border border-[--color-brand-sand] space-y-6">
                  <h2 className="font-display text-base sm:text-lg md:text-2xl font-bold text-[--color-brand-green-dark] mb-4">
                    Vores kontaktinformationer
                  </h2>
                  <div className="space-y-4 font-sans text-sm text-[--color-brand-muted]">
                    <p className="font-bold text-[--color-brand-charcoal]">BP haveservice</p>
                    <p className="flex items-start gap-3">
                      <i className="fa-solid fa-location-dot text-[--color-brand-green-dark] mt-1"></i>
                      <span>Søndergade 111, 5591 Gelsted</span>
                    </p>
                    <p className="flex items-center gap-3">
                      <i className="fa-solid fa-phone text-[--color-brand-green-dark]"></i>
                      <a href="tel:30441611" className="hover:text-[--color-brand-green-dark] transition-colors font-semibold text-[--color-brand-charcoal] whitespace-nowrap">
                        30 44 16 11
                      </a>
                    </p>
                    <p className="flex items-center gap-3">
                      <i className="fa-solid fa-envelope text-[--color-brand-green-dark]"></i>
                      <a href="mailto:post@bp-haveservice.dk" className="hover:text-[--color-brand-green-dark] transition-colors font-semibold text-[--color-brand-charcoal] whitespace-nowrap">
                        post@bp-haveservice.dk
                      </a>
                    </p>
                    <p className="flex items-center gap-3">
                      <i className="fa-solid fa-id-card text-[--color-brand-green-dark]"></i>
                      <span>CVR nr.: 32127347</span>
                    </p>
                  </div>
                </div>
              )}

              {/* Map Placeholder or Trust Box */}
              <div className="bg-[--color-brand-sage-pale]/30 p-8 rounded-3xl border border-[--color-brand-sand] space-y-4">
                <h4 className="font-display text-lg font-bold text-[--color-brand-charcoal]">
                  Lokal service i Gelsted og omegn
                </h4>
                <p className="font-sans text-xs text-[--color-brand-muted] leading-relaxed">
                  Vi dækker Gelsted, Assens, Middelfart, Aarup og de omkringliggende områder på Fyn. Vi kommer gerne ud til dig for at besigtige en opgave og give et uforpligtende tilbud.
                </p>
              </div>
            </div>

            {/* Right Column: Interactive Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
